import { Icon } from "@mdi/react";
import { useEffect, useMemo, useState } from "react";

import {
  mdiContentSave,
  mdiContentSavePlus,
  mdiDelete,
  mdiMinusCircleOutline,
  mdiPencil
} from "@mdi/js";
import { Button, Card, Input, Modal, Popconfirm, Popover, Tooltip } from "antd";
import styled from "styled-components";

import useUser from "hooks/useUser";

import { IconSpinner } from "components/icons";
import { useProjectListQuery } from "components/project/projects/queries";
import { getProjectPermissions } from "components/project/projects/utils/getProjectPermissions";

import { StickFieldsTemplate } from "../types/template";
import { TemplateSaveAs } from "./TemplateSaveAs";

interface TemplatePanelProps {
  templates: StickFieldsTemplate[];
  isLoading: boolean;
  hasChanges: boolean;
  selectedTemplateId: string | undefined;
  hasFields: boolean;
  isReplacingFields: boolean;
  onSelectTemplate: (id: string | undefined) => void;
  onSaveTemplate: (name: string, projectId: string) => void;
  onClearAllFields: () => void;
  onUpdateTemplate: (id: string) => void;
  onRenameTemplate: (id: string, newName: string) => void;
  onDeleteTemplate: (id: string) => void;
}

export const TemplatePanel: React.FC<TemplatePanelProps> = ({
  templates,
  isLoading,
  hasChanges,
  selectedTemplateId,
  hasFields,
  isReplacingFields,
  onSelectTemplate,
  onSaveTemplate,
  onClearAllFields,
  onUpdateTemplate,
  onRenameTemplate,
  onDeleteTemplate
}) => {
  const [isSaveAsOpen, setIsSaveAsOpen] = useState(false);
  const [pendingTemplateId, setPendingTemplateId] = useState<string | undefined>(
    undefined
  );

  const { data: rawProjects } = useProjectListQuery({});
  const { user, isAtLeastPowerUser, isAtLeastAdmin, isReadonly } = useUser();

  const editableProjects = useMemo(() => {
    if (!user || !rawProjects?.length) return [];
    return rawProjects
      .map((p) => ({
        ...p,
        permission: getProjectPermissions(
          p,
          user,
          !!isAtLeastPowerUser,
          !!isAtLeastAdmin,
          !!isReadonly
        )
      }))
      .filter((p) => p.permission.canEditTypeWells);
  }, [rawProjects, user, isAtLeastPowerUser, isAtLeastAdmin, isReadonly]);

  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renameId, setRenameId] = useState<string | undefined>(undefined);
  const [inputName, setInputName] = useState("");
  const [nameError, setNameError] = useState<string | undefined>(undefined);

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  const sortedTemplates = useMemo(
    () => [...templates].sort((a, b) => a.name.localeCompare(b.name)),
    [templates]
  );

  // Clear the pending spinner once the replace finishes
  useEffect(() => {
    if (!isReplacingFields) {
      setPendingTemplateId(undefined);
    }
  }, [isReplacingFields]);

  // Loading a template replaces the current working fields. When there are
  // unsaved changes (including fields with no template selected), confirm first
  // so the user doesn't lose their selection accidentally.
  // Guard against clicks while a replace is already in flight.
  const handleSelectTemplate = (id: string) => {
    if (isReplacingFields) return;
    if (hasChanges) {
      Modal.confirm({
        title: "Discard unsaved field changes?",
        content: "Loading this template will replace your current fields.",
        okText: "Load template",
        cancelText: "Cancel",
        onOk: () => {
          setPendingTemplateId(id);
          onSelectTemplate(id);
        }
      });
    } else {
      setPendingTemplateId(id);
      onSelectTemplate(id);
    }
  };

  const handleConfirmRename = () => {
    const trimmed = inputName.trim();
    if (!trimmed) {
      setNameError("Name is required");
      return;
    }
    if (!renameId) return;
    onRenameTemplate(renameId, trimmed);
    setIsRenameModalOpen(false);
  };

  return (
    <>
      <StyledCard
        title={
          <CardTitleRow>
            <span>Presets</span>
            <TitleActions>
              {isLoading && <IconSpinner />}
              {!isLoading && (
                <Popconfirm
                  title="Clear all fields? This removes every selected field from the scenario."
                  onConfirm={onClearAllFields}
                  okText="Clear"
                  okButtonProps={{ danger: true }}
                  disabled={!hasFields || isReplacingFields}>
                  <Tooltip title="Clear all fields">
                    <IconBtn
                      icon={<Icon path={mdiMinusCircleOutline} size="18px" />}
                      disabled={!hasFields || isReplacingFields}
                    />
                  </Tooltip>
                </Popconfirm>
              )}
              {!isLoading && (
                <Tooltip title="Save changes">
                  <IconBtn
                    icon={<Icon path={mdiContentSave} size="18px" />}
                    disabled={!selectedTemplate || !hasChanges || isReplacingFields}
                    onClick={() =>
                      selectedTemplateId && onUpdateTemplate(selectedTemplateId)
                    }
                  />
                </Tooltip>
              )}
              {!isLoading && (
                <Popover
                  placement="bottomRight"
                  trigger="click"
                  open={isSaveAsOpen}
                  onOpenChange={setIsSaveAsOpen}
                  content={
                    <TemplateSaveAs
                      isOpen={isSaveAsOpen}
                      selectedTemplateName={selectedTemplate?.name ?? ""}
                      editableProjects={editableProjects}
                      onSave={onSaveTemplate}
                      onCancel={() => setIsSaveAsOpen(false)}
                    />
                  }>
                  <Tooltip title="Save as new template">
                    <IconBtn icon={<Icon path={mdiContentSavePlus} size="18px" />} />
                  </Tooltip>
                </Popover>
              )}
            </TitleActions>
          </CardTitleRow>
        }
        bodyStyle={{ padding: 0 }}>
        {sortedTemplates.map((t) => {
          const isSelected = t.id === selectedTemplateId;
          const isPending = pendingTemplateId === t.id && isReplacingFields;
          return (
            <TemplateItem
              key={t.id}
              $selected={isSelected}
              $disabled={isReplacingFields}
              onClick={() => !isReplacingFields && handleSelectTemplate(t.id)}>
              <ItemText>
                <ItemName>{t.name}</ItemName>
                <ItemMeta>{t.fields.length} fields</ItemMeta>
              </ItemText>
              {isPending ? (
                <IconSpinner />
              ) : (
                <ItemActions className="item-actions">
                  <Tooltip title="Rename">
                    <IconBtn
                      size="small"
                      icon={<Icon path={mdiPencil} size="16px" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setRenameId(t.id);
                        setInputName(t.name);
                        setNameError(undefined);
                        setIsRenameModalOpen(true);
                      }}
                    />
                  </Tooltip>
                  <Popconfirm
                    title={`Delete "${t.name}"?`}
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      onDeleteTemplate(t.id);
                    }}
                    onCancel={(e) => e?.stopPropagation()}
                    okText="Delete"
                    okButtonProps={{ danger: true }}>
                    <Tooltip title="Delete">
                      <IconBtn
                        size="small"
                        danger
                        icon={<Icon path={mdiDelete} size="16px" />}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Tooltip>
                  </Popconfirm>
                </ItemActions>
              )}
            </TemplateItem>
          );
        })}

        {!isLoading && sortedTemplates.length === 0 && (
          <EmptyHint>No templates saved</EmptyHint>
        )}
      </StyledCard>

      <Modal
        title="Rename Template"
        open={isRenameModalOpen}
        onOk={handleConfirmRename}
        onCancel={() => setIsRenameModalOpen(false)}
        okText="Rename">
        <Input
          placeholder="Template name"
          value={inputName}
          onChange={(e) => {
            setInputName(e.target.value);
            setNameError(undefined);
          }}
          onPressEnter={handleConfirmRename}
          status={nameError ? "error" : undefined}
        />
        {nameError && <ErrorMsg style={{ marginTop: 4 }}>{nameError}</ErrorMsg>}
      </Modal>
    </>
  );
};

// --- Styled Components ---

const StyledCard = styled(Card)`
  flex: 1;
  display: flex;
  flex-direction: column;

  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;
  }

  .ant-card-head-title {
    width: 100%;
    overflow: visible;
  }

  .ant-card-body {
    flex: 1;
    overflow-y: auto;
    padding: 0;
  }
`;

const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const TitleActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const TemplateItem = styled.div<{ $selected: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 24px;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  background: ${({ $selected }) => ($selected ? "#f0f0f0" : "transparent")};

  &:hover {
    background: ${({ $selected }) => ($selected ? "#f0f0f0" : "#f5f5f5")};
  }

  &:hover .item-actions {
    opacity: 1;
  }
`;

const ItemText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const ItemName = styled.span`
  font-weight: 600;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemMeta = styled.span`
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
`;

const ItemActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  flex-shrink: 0;
  margin-left: 8px;
  transition: opacity 0.15s;
`;

const EmptyHint = styled.div`
  padding: 16px 12px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.35);
  text-align: center;
`;

const IconBtn = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;

  &:not(.ant-btn-dangerous) {
    color: #a2aaad;
    &:hover {
      color: var(--color-text);
    }
  }
`;

const ErrorMsg = styled.div`
  color: #ff4d4f;
  margin-top: 8px;
  font-size: 12px;
`;
